<template>
  <div>
    <label v-if="searchable" class="facet-search">
      <i class="mdi mdi-magnify"></i>
      <input
        v-model="query"
        type="text"
        :placeholder="`Search ${title.toLowerCase()}`"
        @keydown.esc.stop="query = ''"
      />
      <button
        v-if="query"
        type="button"
        class="facet-search-clear"
        title="Clear"
        @click="query = ''"
      >
        <i class="mdi mdi-close"></i>
      </button>
    </label>
    <div
      class="facet-list"
      :class="{
        'is-scroll': visible.length > 12,
        'is-faded': !searching && !showAll && sorted.length > limit
      }"
    >
      <FacetOption
        v-for="option in visible"
        :key="option.value"
        :label="option.label"
        :hint="option.hint"
        :count="option.count"
        :selected="option.selected"
        @toggle="toggle(option)"
      />
    </div>
    <p v-if="searching && !visible.length" class="facet-empty">
      Nothing matches “{{ query.trim() }}”
    </p>
    <button
      v-if="!searching && sorted.length > limit"
      type="button"
      class="facet-more"
      @click="showAll = !showAll"
    >
      <i :class="showAll ? 'mdi mdi-minus' : 'mdi mdi-plus'"></i>
      {{ showAll ? "Show less" : `Show all ${sorted.length}` }}
    </button>
  </div>
</template>
<script>
import FacetOption from "./FacetOption.vue";
import {
  byLabel,
  facetTitle,
  facetValueHint,
  facetValueLabel
} from "./facetLabels";

// Con más opciones que esto (las marcas) se pueden buscar escribiendo.
const SEARCH_MIN_OPTIONS = 8;

// Para buscar da igual mayúsculas, acentos, espacios y puntuación: "nbn"
// encuentra "N.B.N" e "isuzu bvp" encuentra "ISUZUBVP".
const normalize = text =>
  String(text)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export default {
  name: "GenericFilter",
  components: { FacetOption },
  props: {
    options: {
      type: Object,
      required: true
    },
    selection: {
      type: Array,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    // Cuántas se ven antes de "Show all".
    limit: {
      type: Number,
      default: 7
    }
  },
  data() {
    return {
      showAll: false,
      query: ""
    };
  },
  computed: {
    title() {
      return facetTitle(this.type);
    },
    selectedValues() {
      return this.selection
        .filter(item => item.type == this.type)
        .map(item => item.value);
    },
    // Las elegidas primero y después en orden alfabético por lo que se ve.
    sorted() {
      return Object.keys(this.options)
        .map(value => ({
          value,
          label: facetValueLabel(this.type, value),
          hint: facetValueHint(this.type, value),
          count: Number(this.options[value]) || 0,
          selected: this.selectedValues.includes(value)
        }))
        .sort((a, b) => b.selected - a.selected || byLabel(a.label, b.label));
    },
    searchable() {
      return this.sorted.length > SEARCH_MIN_OPTIONS;
    },
    searching() {
      return Boolean(normalize(this.query));
    },
    visible() {
      // Primero las que empiezan por lo escrito ("is": ISUZU antes que AISIN).
      if (this.searching) {
        const text = normalize(this.query);
        const matches = this.sorted
          .map(option => ({
            option,
            at: normalize(option.label).indexOf(text)
          }))
          .filter(match => match.at >= 0);
        return [
          ...matches.filter(match => match.at === 0),
          ...matches.filter(match => match.at > 0)
        ].map(match => match.option);
      }
      return this.showAll ? this.sorted : this.sorted.slice(0, this.limit);
    }
  },
  methods: {
    toggle(option) {
      const selection = {
        type: this.type,
        value: option.value,
        valueLabel: `${this.title}: ${option.label}`
      };
      this.$emit(option.selected ? "removed" : "selected", selection);
      this.query = "";
    }
  }
};
</script>
<style lang="scss" scoped>
.facet-search {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;

  > .mdi-magnify {
    position: absolute;
    left: 8px;
    font-size: 16px;
    color: #9ca3af;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 32px;
    padding: 0 28px 0 28px;
    font-size: 13px;
    color: #111827;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
  }
}

.facet-search-clear {
  position: absolute;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #6b7280;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
}

.facet-list.is-scroll {
  max-height: 340px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

// Hay más debajo: la última opción se desvanece hacia "Show all".
.facet-list.is-faded {
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - 34px),
    rgba(0, 0, 0, 0.25)
  );
  mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - 34px),
    rgba(0, 0, 0, 0.25)
  );
}

.facet-empty {
  padding: 6px;
  font-size: 12px;
  color: #6b7280;
}

.facet-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  border: 1px solid #dbeafe;
  border-radius: 5px;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
  }
}
</style>
